<template>
  <Teleport to="body">
    <div v-if="show" ref="panelRef" class="message-reaction-picker" :style="panelStyle">
      <div class="message-reaction-picker__row" v-if="!showFullPicker">
        <button
          v-for="emoji in quickReactions"
          :key="emoji"
          type="button"
          class="message-reaction-picker__emoji-btn"
          @click="selectEmoji(emoji)"
        >
          {{ emoji }}
        </button>
        <button
          type="button"
          class="message-reaction-picker__expand-btn"
          title="More emojis"
          @click="showFullPicker = true"
        >
          <Icon icon="mdi:plus" />
        </button>
      </div>
      <EmojiPicker v-else @select="handleFullPickerSelect" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import EmojiPicker from "./EmojiPicker.vue";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const props = defineProps<{
  show: boolean;
  triggerEl: HTMLElement | null;
}>();

const emit = defineEmits<{
  select: [emoji: string];
  close: [];
}>();

const quickReactions = QUICK_REACTIONS;
const showFullPicker = ref(false);
const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({
  position: "fixed",
  zIndex: "11000",
  visibility: "hidden",
});

function selectEmoji(emoji: string) {
  emit("select", emoji);
}

function handleFullPickerSelect(payload: { emoji: string; name: string }) {
  emit("select", payload.emoji);
}

function updatePosition() {
  if (!props.triggerEl || !panelRef.value) return;

  const triggerRect = props.triggerEl.getBoundingClientRect();
  const panelWidth = panelRef.value.offsetWidth || 260;
  const panelHeight = panelRef.value.offsetHeight || 48;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 8;

  const spaceBelow = vh - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  const showAbove = spaceAbove > spaceBelow && spaceAbove >= panelHeight + gap;

  let top = showAbove ? triggerRect.top - panelHeight - gap : triggerRect.bottom + gap;
  top = Math.max(8, Math.min(top, vh - panelHeight - 8));

  let left = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;
  left = Math.max(8, Math.min(left, vw - panelWidth - 8));

  panelStyle.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    zIndex: "11000",
    visibility: "visible",
  };
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node;
  if (panelRef.value?.contains(target)) return;
  if (props.triggerEl?.contains(target)) return;
  emit("close");
}

watch(
  () => props.show,
  async (val) => {
    if (val) {
      showFullPicker.value = false;
      panelStyle.value = {
        position: "fixed",
        zIndex: "11000",
        visibility: "hidden",
        top: "-9999px",
        left: "-9999px",
      };
      await nextTick();
      requestAnimationFrame(() => {
        updatePosition();
      });
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      document.addEventListener("mousedown", handleClickOutside, true);
    } else {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside, true);
    }
  }
);

watch(showFullPicker, async () => {
  await nextTick();
  requestAnimationFrame(() => {
    updatePosition();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
  document.removeEventListener("mousedown", handleClickOutside, true);
});
</script>

<style scoped>
.message-reaction-picker {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  border-radius: 24px;
  background: #fff;
}

.message-reaction-picker__row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
}

.message-reaction-picker__emoji-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.25rem;
  transition:
    transform 0.15s ease,
    background 0.15s ease;
}

.message-reaction-picker__emoji-btn:hover {
  background: #f3f4f6;
  transform: scale(1.25);
}

.message-reaction-picker__expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #6b7280;
  font-size: 1.1rem;
  transition: all 0.15s ease;
}

.message-reaction-picker__expand-btn:hover {
  background: #f3f4f6;
  color: #111827;
}
</style>
